import { logger } from "@/utils/logger";
import { useMemo } from "react";
import useSWR from "swr";
import {
  checkUnreadInAppNotificationsForEmployer,
  fetchLatestInAppNotificationsForEmployer,
  getLastCheckedForEmployer,
  updateLastCheckedForEmployer,
} from "./in-app-notifications";

const refreshIntervalMs = 300000; // 5分間隔でポーリング

export function useInAppNotifications({ officeId }: { officeId: string }) {
  // 未読状態の管理
  const { data: unreadData, mutate: mutateUnread } = useSWR(
    `inAppNotifications-unread-${officeId}`,
    () => checkUnreadInAppNotificationsForEmployer({ officeId }),
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: refreshIntervalMs,
    },
  );

  // 最新通知の管理
  const { data: notificationsData, isLoading } = useSWR(
    `inAppNotifications-latest-${officeId}`,
    () => fetchLatestInAppNotificationsForEmployer({ officeId }),
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: refreshIntervalMs,
    },
  );

  // 最終確認時刻の管理
  const { data: lastCheckedData, mutate: mutateLastChecked } = useSWR(
    `inAppNotifications-lastChecked-${officeId}`,
    () => getLastCheckedForEmployer({ officeId }),
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      refreshInterval: refreshIntervalMs,
    },
  );

  const notifications = useMemo(
    () => (notificationsData?.success ? notificationsData.value : []),
    [notificationsData],
  );

  const lastCheckedAt = lastCheckedData?.success
    ? lastCheckedData.value.lastCheckedAt
    : null;

  const hasUnread = unreadData?.success ? unreadData.value.hasUnread : false;

  const markAsReadAndUpdateLastChecked = () => {
    // キャッシュを既読に更新
    mutateUnread(
      { success: true, value: { hasUnread: false } },
      { revalidate: false },
    );

    // 最終確認時刻を更新（キャッシュは更新しない）
    updateLastCheckedForEmployer({ officeId }).catch((error) => {
      logger.error({ error, officeId }, "Failed to update last checked");
    });
  };

  const refreshLastChecked = () => {
    // 最終確認時刻のキャッシュを再取得
    mutateLastChecked();
  };

  return {
    hasUnread,
    notifications,
    isLoading,
    lastCheckedAt,
    markAsReadAndUpdateLastChecked,
    refreshLastChecked,
  };
}
